<script setup lang="ts">
import {
  cellphoneInputMask,
  cellphoneToApiPayload,
  formatCellphoneMasked,
  formatCellphoneTyping,
  normalizeCellphoneDigits,
} from '@solfacil/frontend-utils/cellphone';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import {
  computed, nextTick, ref, watch,
} from 'vue';

import FormFieldSkeleton from '~/components/shared/FormFieldSkeleton.vue';
import ModalForm from '~/components/shared/ModalForm.vue';
import useFormApiErrors from '~/composables/useFormApiErrors';
import useCoreUser, { type CoreApiUser } from '~/store/coreUsers/useCoreUser';
import { useCreatePartnerUser, useUpdatePartnerUser } from '~/store/coreUsers/useCoreUserMutations';
import { type CoreUserFormField, resolveCoreUserFormApiErrors } from '~/utils/apiFieldErrors';

import {
  createUserFormSchema,
  DEFAULT_USER_PROFILE,
  getCreateUserProfile,
  type UserFormData,
  userFormSchema,
} from '../schemas/userSchema';

export interface UserModalProps {
  mode: 'create' | 'edit' | 'view';
  modelValue: boolean;
  partnerId: number;
  partnerProfile?: string;
  userId?: null | number;
}

type EditableField = 'celular' | 'email' | 'nomeCompleto';

const CREATE_EDITABLE_FIELDS: EditableField[] = ['nomeCompleto', 'email', 'celular'];
const UPDATE_EDITABLE_FIELDS: EditableField[] = ['nomeCompleto', 'celular'];

const props = withDefaults(defineProps<UserModalProps>(), {
  partnerProfile: undefined,
  userId: null,
});

const emit = defineEmits<{
  success: [];
  'update:modelValue': [value: boolean];
}>();

const modalFormRef = ref();
const selectedUserId = ref<null | number>(props.userId);

const createMutation = useCreatePartnerUser();
const updateMutation = useUpdatePartnerUser();

const userIdRef = computed(() => (
  (props.mode === 'edit' || props.mode === 'view') ? selectedUserId.value : null
));

const { data: fetchedUser, isLoading: isLoadingUser } = useCoreUser(userIdRef);

const validationSchema = computed(() => (
  props.mode === 'create'
    ? toTypedSchema(createUserFormSchema)
    : toTypedSchema(userFormSchema)
));

const {
  defineField,
  errors,
  handleSubmit,
  resetForm,
  setErrors,
  setValues,
} = useForm<UserFormData>({
  initialValues: {
    celular: '',
    email: '',
    nomeCompleto: '',
  },
  validateOnMount: false,
  validationSchema,
});

const fieldOptions = {
  validateOnBlur: true,
  validateOnChange: true,
  validateOnInput: true,
  validateOnModelUpdate: true,
};

const [nomeCompleto, nomeCompletoProps] = defineField('nomeCompleto', fieldOptions);
const [celular, celularProps] = defineField('celular', fieldOptions);
const [email, emailProps] = defineField('email', fieldOptions);

const { applyFormApiErrors, clearFormApiError, formApiError } = useFormApiErrors<CoreUserFormField>(setErrors);

function clearFieldApiError(field: CoreUserFormField) {
  clearFormApiError();
  setErrors({ [field]: undefined });
}

const celularMasked = computed({
  get: () => celular.value,
  set: (value: string) => {
    celular.value = formatCellphoneTyping(value);
  },
});

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const modalTitle = computed(() => {
  switch (props.mode) {
    case 'create':
      return 'Adicionar usuário';
    case 'edit':
      return 'Editar usuário';
    case 'view':
      return 'Detalhes do usuário';
    default:
      return 'Usuário';
  }
});

const isSubmitting = computed(() => (
  createMutation.isPending.value || updateMutation.isPending.value
));

const isFetchingUser = computed(() => (
  isLoadingUser.value && (props.mode === 'edit' || props.mode === 'view')
));

const showFormSkeleton = computed(() => isFetchingUser.value);

function applyUserToForm(user: CoreApiUser) {
  const rawCelular = user.contato.celular || '';
  setValues({
    celular: rawCelular ? formatCellphoneMasked(normalizeCellphoneDigits(rawCelular)) : '',
    email: user.contato.email || '',
    nomeCompleto: user.nomeCompleto || '',
  });
}

function isFieldEnabled(
  field: EditableField,
  isCreateMode: boolean,
  isEditMode: boolean,
  isSubmittingForm: boolean,
): boolean {
  if (isSubmittingForm) return false;
  if (isCreateMode) return CREATE_EDITABLE_FIELDS.includes(field);
  if (isEditMode) return UPDATE_EDITABLE_FIELDS.includes(field);
  return false;
}

watch(() => props.userId, (newId) => {
  selectedUserId.value = newId;
});

watch(() => fetchedUser.value, (user) => {
  if (user && (props.mode === 'edit' || props.mode === 'view')) {
    nextTick(() => applyUserToForm(user));
  }
}, { immediate: true });

watch([nomeCompleto, email, celular], () => {
  clearFormApiError();
});

watch(() => props.modelValue, (isOpen) => {
  if (!isOpen) {
    nextTick(() => {
      clearFormApiError();
      resetForm();
      selectedUserId.value = null;
    });
    return;
  }

  clearFormApiError();

  selectedUserId.value = props.userId;

  if (props.mode === 'create') {
    nextTick(() => {
      resetForm();
      setValues({
        celular: '',
        email: '',
        nomeCompleto: '',
      });
    });
  } else if (fetchedUser.value) {
    nextTick(() => applyUserToForm(fetchedUser.value));
  }
});

const handleSave = handleSubmit(async (values) => {
  try {
    if (props.mode === 'create') {
      await createMutation.mutateAsync({
        contato: {
          celular: values.celular ? cellphoneToApiPayload(values.celular) : '',
          email: values.email || '',
        },
        nomeCompleto: values.nomeCompleto,
        parceiroId: props.partnerId,
        perfil: getCreateUserProfile(props.partnerProfile),
      });
    } else if (selectedUserId.value) {
      await updateMutation.mutateAsync({
        data: {
          contato: {
            celular: values.celular ? cellphoneToApiPayload(values.celular) : '',
            email: values.email || '',
          },
          nomeCompleto: values.nomeCompleto,
          perfil: fetchedUser.value?.perfil || DEFAULT_USER_PROFILE,
        },
        id: selectedUserId.value,
        partnerId: props.partnerId,
      });
    }

    emit('success');
    modalFormRef.value?.resetEditingState();
    isVisible.value = false;
  } catch (error) {
    const fallback = props.mode === 'create'
      ? 'Erro ao cadastrar usuário'
      : 'Erro ao atualizar usuário';
    applyFormApiErrors(error, (err) => resolveCoreUserFormApiErrors(err, fallback));
  }
});

function handleCancel() {
  isVisible.value = false;
}

function handleEdit() {
  emit('update:modelValue', false);
  nextTick(() => {
    emit('update:modelValue', true);
  });
}
</script>

<template>
  <ModalForm
    ref="modalFormRef"
    v-model="isVisible"
    :title="modalTitle"
    :mode="mode"
    :loading="isSubmitting"
    size="lg"
    @save="handleSave"
    @edit="handleEdit"
    @cancel="handleCancel"
  >
    <template
      #default="{
        isEditMode: modalIsEditMode,
        isCreateMode: modalIsCreateMode,
        isLoading: isSubmittingForm,
      }"
    >
      <div v-if="showFormSkeleton" class="row">
        <div class="col-md-6">
          <FormFieldSkeleton />
        </div>
        <div class="col-md-6">
          <FormFieldSkeleton />
        </div>
        <div class="col-md-6">
          <FormFieldSkeleton no-margin />
        </div>
      </div>

      <div v-else class="row">
        <div v-if="formApiError" class="col-12">
          <div class="alert alert-danger mb-3 py-2" role="alert">
            {{ formApiError }}
          </div>
        </div>

        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label required" for="nomeCompleto">Nome completo</label>
            <input
              id="nomeCompleto"
              v-model="nomeCompleto"
              v-bind="nomeCompletoProps"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.nomeCompleto }"
              :disabled="!isFieldEnabled('nomeCompleto', modalIsCreateMode, modalIsEditMode, isSubmittingForm)"
              @input="clearFieldApiError('nomeCompleto')"
            />
            <div v-if="errors.nomeCompleto" class="invalid-feedback">
              {{ errors.nomeCompleto }}
            </div>
          </div>
        </div>

        <div class="col-md-6">
          <div class="mb-3">
            <label class="form-label" :class="{ required: modalIsCreateMode }" for="email">E-mail</label>
            <input
              id="email"
              v-model="email"
              v-bind="emailProps"
              type="email"
              class="form-control"
              :class="{ 'is-invalid': errors.email }"
              :disabled="!isFieldEnabled('email', modalIsCreateMode, modalIsEditMode, isSubmittingForm)"
              @input="clearFieldApiError('email')"
            />
            <div v-if="errors.email" class="invalid-feedback">
              {{ errors.email }}
            </div>
          </div>
        </div>

        <div class="col-md-6">
          <div class="mb-0">
            <label
              class="form-label"
              :class="{ required: modalIsCreateMode || modalIsEditMode }"
              for="celular"
            >Celular</label>
            <input
              id="celular"
              v-model="celularMasked"
              v-bind="celularProps"
              type="tel"
              inputmode="numeric"
              class="form-control"
              :class="{ 'is-invalid': errors.celular }"
              :placeholder="cellphoneInputMask"
              maxlength="15"
              :disabled="!isFieldEnabled('celular', modalIsCreateMode, modalIsEditMode, isSubmittingForm)"
              @input="clearFieldApiError('celular')"
            />
            <div v-if="errors.celular" class="invalid-feedback">
              {{ errors.celular }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </ModalForm>
</template>
